import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';

export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-brand-bg flex flex-col overflow-x-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[55vw] h-[55vw] bg-orange-500/[0.07] rounded-full blur-3xl" />
        <div className="absolute top-[20%] right-[-10%] w-[60vw] h-[60vw] bg-red-500/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[20%] w-[50vw] h-[50vw] bg-orange-500/[0.05] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />

        <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 text-sm hover:text-orange-400 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-slate-400 text-sm mb-10">Last updated: January 2025</p>

          <div className="space-y-8 text-slate-300 text-sm sm:text-base leading-relaxed [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-4 [&_h2]:mt-8 [&_h3]:text-base [&_h3]:sm:text-lg [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-3 [&_h3]:mt-6 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-2 [&_a]:text-orange-400 [&_a]:hover:underline [&_strong]:text-white">
            <section>
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing or using VALUEBOT ("the Service"), operated by Valuebell ("we", "our", or "us"),
                you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2>2. Service Description</h2>
              <p>
                VALUEBOT is a podcast transcription and show notes service that:
              </p>
              <ul>
                <li>Transcribes audio and video files from Google Drive</li>
                <li>Generates show notes including episode titles, descriptions, chapter markers, social media quotes, and editing suggestions</li>
                <li>Creates Google Docs with your transcripts and show notes</li>
              </ul>
            </section>

            <section>
              <h2>3. Eligibility</h2>
              <p>
                You must be at least 18 years old to use the Service. By using VALUEBOT, you represent and warrant that you meet this age requirement.
              </p>
            </section>

            <section>
              <h2>4. Account and Authentication</h2>
              <ul>
                <li>You must sign in with a valid Google account to use the Service</li>
                <li>You are responsible for maintaining the security of your Google account</li>
                <li>You agree to provide accurate information and not impersonate others</li>
              </ul>
            </section>

            <section>
              <h2>5. User Responsibilities</h2>
              <p>You agree to:</p>
              <ul>
                <li><strong>Own or have rights to your content:</strong> You must have the legal right to submit any audio or video files for processing</li>
                <li><strong>Ensure proper permissions:</strong> Make sure your Google Drive files have appropriate sharing settings that allow our service to access them</li>
                <li><strong>Not submit prohibited content:</strong> Do not submit content that is illegal, harmful, threatening, abusive, defamatory, or violates any third party's rights</li>
                <li><strong>Use the Service lawfully:</strong> Comply with all applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h2>6. Intellectual Property</h2>

              <h3>Your Content</h3>
              <p>
                You retain full ownership of all audio, video, and other content you submit to the Service.
                By submitting content, you grant us a limited, non-exclusive license to process your content solely for the purpose of providing the Service.
              </p>

              <h3>Generated Content</h3>
              <p>
                Transcripts, show notes, and other outputs generated by the Service from your content belong to you.
                You are free to use, modify, and distribute these outputs as you see fit.
              </p>

              <h3>Our Service</h3>
              <p>
                The VALUEBOT service, including its design, features, and underlying technology, is owned by Valuebell.
                These Terms do not grant you any rights to our trademarks, logos, or other brand features.
              </p>
            </section>

            <section>
              <h2>7. Service Availability and Limitations</h2>
              <ul>
                <li>We strive to provide reliable service but do not guarantee 100% uptime</li>
                <li>Processing times vary based on file length and server load</li>
                <li>Maximum supported file duration is 10 hours</li>
                <li>We may impose usage limits to ensure fair access for all users</li>
                <li>We reserve the right to modify or discontinue the Service at any time</li>
              </ul>
            </section>

            <section>
              <h2>8. Disclaimer of Warranties</h2>
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
              <p>We do not warrant that:</p>
              <ul>
                <li>Transcriptions will be 100% accurate</li>
                <li>Show notes will meet your specific needs</li>
                <li>The Service will be uninterrupted or error-free</li>
                <li>Results will be suitable for any particular purpose</li>
              </ul>
            </section>

            <section>
              <h2>9. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, VALUEBELL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY,
                OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul>
                <li>Your use or inability to use the Service</li>
                <li>Any errors or inaccuracies in transcriptions or show notes</li>
                <li>Unauthorized access to or alteration of your content</li>
                <li>Any third-party conduct on the Service</li>
              </ul>
            </section>

            <section>
              <h2>10. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless Valuebell and its officers, directors, employees, and agents
                from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
              </p>
              <ul>
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third party's rights</li>
                <li>Content you submit to the Service</li>
              </ul>
            </section>

            <section>
              <h2>11. Termination</h2>
              <ul>
                <li>You may stop using the Service at any time</li>
                <li>We may suspend or terminate your access if you violate these Terms</li>
                <li>Upon termination, your right to use the Service ceases immediately</li>
                <li>You may request deletion of your data as described in our Privacy Policy</li>
              </ul>
            </section>

            <section>
              <h2>12. Changes to Terms</h2>
              <p>
                We may update these Terms from time to time. We will notify you of significant changes by posting a notice on our website.
                Your continued use of the Service after changes take effect constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2>13. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Israel,
                without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject
                to the exclusive jurisdiction of the courts located in Israel.
              </p>
            </section>

            <section>
              <h2>14. Contact Us</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p>
                <strong>Email:</strong> <a href="mailto:support@valuebell.studio">support@valuebell.studio</a><br />
                <strong>Company:</strong> Valuebell Studio<br />
                <strong>Location:</strong> Israel
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};
